<template>
  <v-card class="eudr-manager">
    <v-card-title class="primary white--text">
      <span class="headline">{{ $t('eudrManager') }}</span>
      <v-spacer/>
      <Icon
        dataTestId="eudr-manager-close"
        icon="mdi-close"
        iconColor="white"
        :tooltipText="$t('close')"
        :small="false"
        :large="true"
        @icon-clicked="$emit('close')"
      />
    </v-card-title>

    <v-card-text class="pt-4">
      <v-row align="center" dense class="mt-1 mb-2">
        <v-col cols="auto">
          <v-select v-model="year" :label="$t('year')" :items="yearOptions" dense hide-details class="eudr-period-select"/>
        </v-col>
        <v-col cols="auto">
          <v-select v-model="month" :label="$t('month')" :items="monthOptions" item-text="text" item-value="value" dense hide-details class="eudr-period-select--month"/>
        </v-col>
        <v-col cols="auto">
          <Icon
            icon="mdi-refresh"
            :tooltipText="$t('refresh')"
            :small="false"
            elementId="eudr-manager-refresh"
            @icon-clicked="loadAll"
          />
        </v-col>
      </v-row>

      <v-row>
        <!-- Internal accounts with reportable production this period -->
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-medium">{{ $t('eudrInternalAccounts') }}</v-card-title>
            <v-card-subtitle class="caption">{{ $t('eudrInternalAccountsInfo') }}</v-card-subtitle>
            <v-card-text>
              <DataTable
                :items="internalAccountRows"
                :headers="internalHeaders"
                :loading="overviewLoading"
                :selected.sync="selectedInternalAccounts"
                :allSelected="allInternalSelected"
                showSelect
                keepSelectionWhenItemsChange
                tableKey="accountId"
                defaultSort="displayName"
                :showSearch="false"
                hideHeader
                @select-all-clicked="toggleSelectAllInternal"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- External EUDR reports uploaded this period -->
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="subtitle-1 font-weight-medium">{{ $t('eudrExternalFiles') }}</v-card-title>
            <v-card-subtitle class="caption">{{ $t('eudrExternalFilesInfo') }}</v-card-subtitle>
            <v-card-text>
              <DataTable
                :items="externalReportRows"
                :headers="externalHeaders"
                :loading="overviewLoading"
                :customCells="externalCustomColumns"
                :selected.sync="selectedExternalReports"
                :allSelected="allExternalSelected"
                showSelect
                keepSelectionWhenItemsChange
                tableKey="eudrBatchId"
                defaultSort="createdAt"
                :sortDesc="true"
                :showSearch="false"
                hideHeader
                @select-all-clicked="toggleSelectAllExternal"
              >
                <template #status="{ item }">
                  <EudrBatchStatusIcon :status="item.status"/>
                </template>

                <template #actions="{ item }">
                  <Icon
                    icon="mdi-table-search"
                    iconColor="primary"
                    elementId="eudr-external-view"
                    :tooltipText="$t('viewDetails')"
                    :disabled="item.status !== EudrBatchStatus.Complete.value || !item.resultBlobPath || loadingDetails"
                    @icon-clicked="openDetails(item)"
                  />
                </template>
              </DataTable>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- External suppliers still owed a report -->
      <v-card v-if="missingExternalAccounts.length" outlined class="mt-2">
        <v-card-title class="subtitle-1 font-weight-medium">{{ $t('eudrMissingExternals') }}</v-card-title>
        <v-card-subtitle class="caption">{{ $t('eudrMissingExternalsInfo') }}</v-card-subtitle>
        <v-card-text>
          <DataTable
            :items="missingAccountRows"
            :headers="missingHeaders"
            :loading="overviewLoading"
            :customCells="missingCustomColumns"
            tableKey="accountId"
            defaultSort="displayName"
            :showSearch="false"
            hideHeader
          >
            <template #actions="{ item }">
              <Icon
                icon="mdi-cloud-upload"
                iconColor="primary"
                elementId="eudr-upload-missing"
                :tooltipText="$t('attachExternal')"
                @icon-clicked="openUploadForMissing(item)"
              />
              <Icon
                icon="mdi-email-arrow-right-outline"
                iconColor="primary"
                elementId="eudr-request-file"
                :tooltipText="item.hasEudrContact ? $t('eudrRequestFile') : $t('eudrRequestFileNoContact')"
                :disabled="!item.hasEudrContact"
                @icon-clicked="promptRequestFile(item)"
              />
            </template>
          </DataTable>
        </v-card-text>
      </v-card>

      <div class="subtitle-1 font-weight-medium mt-4 mb-1">{{ $t('eudrComposites') }}</div>
      <v-alert v-if="pollTimedOut && anyCompositeProcessing" dense text type="info" class="mb-1">
        {{ $t('eudrPollTimedOut') }}
      </v-alert>
      <DataTable
        :items="compositeRows"
        :headers="compositeHeaders"
        :loading="compositesLoading"
        :customCells="compositeCustomColumns"
        tableKey="eudrBatchId"
        defaultSort="createdAt"
        :sortDesc="true"
        :showSearch="false"
        hideHeader
      >
        <template #status="{ item }">
          <div class="d-flex align-center justify-center">
            <EudrBatchStatusIcon :status="item.status"/>
            <Icon
              v-if="item.status === EudrBatchStatus.Error.value && item.errorSummary"
              icon="mdi-alert-circle-outline"
              iconColor="error"
              :tooltipText="item.errorSummary"
            />
            <Icon
              v-if="item.status === EudrBatchStatus.NeedsValidation.value && item.conflictCount > 0"
              icon="mdi-alert"
              iconColor="warning"
              :tooltipText="$t('eudrConflictsBadge', { count: item.conflictCount })"
            />
          </div>
        </template>

        <template #actions="{ item }">
          <div class="d-flex align-center justify-center flex-nowrap">
            <Icon
              icon="mdi-clipboard-check-outline"
              iconColor="primary"
              elementId="eudr-composite-review"
              :tooltipText="$t('eudrReviewValidate')"
              :disabled="!hasResult(item)"
              @icon-clicked="openReview(item)"
            />
            <Icon
              icon="mdi-table-search"
              iconColor="primary"
              elementId="eudr-composite-view"
              :tooltipText="$t('viewDetails')"
              :disabled="!hasResult(item) || loadingDetails"
              @icon-clicked="openDetails(item)"
            />
            <!-- Menu activator mirrors the Icon wrapper's markup so it sits flush with its siblings. -->
            <v-menu offset-y>
              <template #activator="{ on: menu, attrs }">
                <div style="display: inline-block;" class="mr-1">
                  <v-tooltip bottom color="success" :disabled="!hasResult(item)">
                    <template #activator="{ on: tooltip }">
                      <v-icon
                        small
                        color="success"
                        id="eudr-composite-download"
                        style="cursor: pointer;"
                        :disabled="!hasResult(item) || downloading"
                        v-bind="attrs"
                        v-on="{ ...menu, ...tooltip }"
                      >mdi-download</v-icon>
                    </template>
                    <span class="subtitle-1 white--text">{{ $t('download') }}</span>
                  </v-tooltip>
                </div>
              </template>
              <v-list dense>
                <v-list-item :disabled="!isFinalized(item)" @click="downloadSubmission(item)">
                  <v-list-item-icon class="mr-2 my-auto">
                    <v-icon small :color="isFinalized(item) ? 'success' : undefined">mdi-lock-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t('eudrDownloadSubmission') }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ isFinalized(item) ? $t('eudrDownloadSubmissionReady', { date: finalizedDateLabel(item) }) : $t('eudrDownloadSubmissionHint') }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item @click="downloadShare(item)">
                  <v-list-item-icon class="mr-2 my-auto">
                    <v-icon small color="primary">mdi-share-variant</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ $t('eudrDownloadShare') }}</v-list-item-title>
                    <v-list-item-subtitle>{{ $t('eudrDownloadShareHint') }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
            <Icon
              icon="mdi-email-fast-outline"
              iconColor="primary"
              elementId="eudr-composite-send"
              :tooltipText="isFinalized(item) ? $t('eudrSendToContacts') : $t('eudrSendRequiresFinalize')"
              :disabled="!isFinalized(item)"
              @icon-clicked="openSendDialog(item)"
            />
            <Icon
              icon="mdi-delete"
              iconColor="error"
              elementId="eudr-composite-delete"
              :tooltipText="$t('delete')"
              :disabled="deleting"
              @icon-clicked="promptDelete(item)"
            />
          </div>
        </template>
      </DataTable>
    </v-card-text>

    <v-card-actions>
      <v-spacer/>
      <v-btn
        color="primary"
        id="eudr-create-composite"
        :disabled="!canCreateComposite || compositing"
        @click="openCompositePrompt"
      >
        <v-icon left>mdi-layers-triple</v-icon>
        {{ $t('createComposite') }}
      </v-btn>
    </v-card-actions>

    <Dialog :stateId="compositeDialogId" @close="cancelComposite" @click:outside="cancelComposite" @dialog-closing="cancelComposite">
      <v-card v-if="compositing || promptingComposite">
        <v-card-title class="primary white--text">{{ $t('createComposite') }}</v-card-title>
        <v-card-text class="pt-4">
          <p class="caption grey--text text--darken-1">{{ $t('eudrCompositeSummary', { internal: selectedInternalAccounts.length, external: selectedExternalReports.length }) }}</p>
          <v-text-field
            v-model="compositeLabel"
            :label="$t('compositeLabel')"
            autofocus
            hide-details
          />
        </v-card-text>
        <v-card-actions class="px-4 pb-3">
          <v-spacer/>
          <v-btn text @click="cancelComposite">{{ $t('cancel') }}</v-btn>
          <v-btn
            color="primary"
            :loading="compositing"
            :disabled="compositing || !compositeLabel"
            @click="createCompositePackage"
          >{{ $t('createComposite') }}</v-btn>
        </v-card-actions>
      </v-card>
    </Dialog>

    <!-- Upload for a missing external account -->
    <Dialog :stateId="uploadDialogId" @close="cancelUpload" @click:outside="cancelUpload" @dialog-closing="cancelUpload">
      <v-card v-if="uploadingForAccount">
        <v-card-title class="primary white--text">
          <span class="headline">{{ addNameWithCode(uploadingForAccount.name, uploadingForAccount.code) }}</span>
          <v-spacer/>
          <Icon icon="mdi-close" iconColor="white" :tooltipText="$t('close')" :small="false" :large="true" @icon-clicked="cancelUpload"/>
        </v-card-title>
        <v-card-text class="pt-4">
          <EudrUploadPanel
            :accountId="uploadingForAccount.accountId"
            :startDate="period.startDate"
            :endDate="period.endDate"
            @uploaded="onUploadedForMissing"
          />
        </v-card-text>
      </v-card>
    </Dialog>

    <Dialog :stateId="detailDialogId" @close="closeDetails" @click:outside="closeDetails" @dialog-closing="closeDetails">
      <EudrDetailDialog
        v-if="viewingDetails"
        :tracts="detailTracts"
        :featureCollection="detailFeatureCollection"
        :sources="detailSources"
        :title="detailTitle"
        @close="closeDetails"
      />
    </Dialog>

    <Dialog :stateId="validationDialogId" :confirmClose="!!reviewingReport" @close="onReviewClosed" @dialog-closing="onReviewClosed">
      <EudrValidationDialog
        v-if="reviewingReport"
        :report="reviewingReport"
        @close="closeReview"
        @saved="onValidationSaved"
        @refresh="loadComposites"
      />
    </Dialog>

    <Dialog :stateId="deleteDialogId" @close="cancelDelete" @click:outside="cancelDelete" @dialog-closing="cancelDelete">
      <ConfirmDelete
        v-if="reportToDelete"
        :title="$t('eudrPackage')"
        initialFocus="cancel"
        @delete="deleteSelectedReport"
        @cancel-delete="cancelDelete"
      />
    </Dialog>

    <!-- Email the finalized share file to byproduct buyers' EUDR contacts -->
    <Dialog :stateId="sendDialogId" @close="closeSendDialog" @click:outside="closeSendDialog" @dialog-closing="closeSendDialog">
      <EudrSendDialog
        v-if="sendingReport"
        :report="sendingReport"
        @close="closeSendDialog"
        @sent="closeSendDialog"
      />
    </Dialog>

    <!-- Confirm emailing a file request to a supplier's EUDR contact(s) -->
    <Dialog :stateId="requestDialogId" @close="cancelRequestFile" @click:outside="cancelRequestFile" @dialog-closing="cancelRequestFile">
      <ConfirmDialog
        v-if="requestingFileFor"
        :title="$t('eudrRequestFileTitle')"
        :body="$t('eudrRequestFileBody', { account: requestingFileFor.name, period: periodLabel })"
        :confirmText="$t('eudrRequestFileConfirm')"
        @cancel="cancelRequestFile"
        @confirm="sendFileRequest"
      />
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { EudrBatchStatus, EudrBatchKind } from '@/utils/Enumerations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { eudrClient } from '@/utils/EudrClient.js'
import { buildShareFile } from '@/utils/eudr/EudrExport.js'
import { loadReportDetail, hasUsableResult } from '@/utils/eudr/EudrDetail.js'
import { monthYearToRange, eudrYearOptions, eudrMonthOptions, windowEndDisplay } from '@/utils/eudr/EudrPeriod.js'

const POLL_INTERVAL_MS = 5000
const MAX_POLL_ATTEMPTS = 120

export default {
  name: 'EudrManagerDialog',

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    EudrUploadPanel: () => import('@/components/eudr/EudrUploadPanel.vue'),
    EudrBatchStatusIcon: () => import('@/components/eudr/EudrBatchStatusIcon.vue'),
    EudrDetailDialog: () => import('@/components/eudr/EudrDetailDialog.vue'),
    EudrValidationDialog: () => import('@/components/eudr/EudrValidationDialog.vue'),
    EudrSendDialog: () => import('@/components/eudr/EudrSendDialog.vue'),
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue')
  },

  data () {
    const now = new Date()
    return {
      EudrBatchStatus,
      currentYear: now.getFullYear(),
      currentMonth: now.getMonth() + 1,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      internalAccounts: [],
      externalReports: [],
      missingExternalAccounts: [],
      composites: [],
      selectedInternalAccounts: [],
      selectedExternalReports: [],
      overviewLoading: false,
      compositesLoading: false,
      promptingComposite: false,
      compositing: false,
      compositeLabel: '',
      uploadingForAccount: null,
      reviewingReport: null,
      viewingDetails: false,
      detailTracts: [],
      detailFeatureCollection: null,
      detailSources: [],
      detailTitle: '',
      loadingDetails: false,
      reportToDelete: null,
      sendingReport: null,
      requestingFileFor: null,
      downloading: false,
      deleting: false,
      pollTimer: null,
      pollAttempts: 0,
      // Automatic polling gave up (MAX_POLL_ATTEMPTS) while something was still processing.
      pollTimedOut: false,
      compositeDialogId: uniqueDialogId('eudr-mgr-composite-dialog'),
      uploadDialogId: uniqueDialogId('eudr-mgr-upload-dialog'),
      detailDialogId: uniqueDialogId('eudr-mgr-detail-dialog'),
      validationDialogId: uniqueDialogId('eudr-mgr-validation-dialog'),
      deleteDialogId: uniqueDialogId('eudr-mgr-delete-dialog'),
      sendDialogId: uniqueDialogId('eudr-mgr-send-dialog'),
      requestDialogId: uniqueDialogId('eudr-mgr-request-dialog')
    }
  },

  computed: {
    ...mapGetters('account', ['allAccounts']),

    yearOptions () {
      return eudrYearOptions(this.currentYear)
    },

    monthOptions () {
      return eudrMonthOptions(this.year, this.currentYear, this.currentMonth)
    },

    period () {
      return monthYearToRange(this.year, this.month)
    },

    periodLabel () {
      return `${utcToLocalDate(this.period.startDate, 'MM/DD/YYYY')} – ${windowEndDisplay(this.period.endDate)}`
    },

    internalAccountRows () {
      return this.internalAccounts.map(a => ({ ...a, displayName: this.addNameWithCode(a.name, a.code) }))
    },

    missingAccountRows () {
      return this.missingExternalAccounts.map(a => ({ ...a, displayName: this.addNameWithCode(a.name, a.code) }))
    },

    externalReportRows () {
      // The store lookup is only a fallback: it holds whatever slice the Accounts table last fetched.
      return this.externalReports.map(r => ({ ...r, accountName: r.destinationAccountName || this.accountName(r.destinationAccountId), window: this.windowLabel(r) }))
    },

    compositeRows () {
      return this.composites.map(r => ({ ...r, window: this.windowLabel(r) }))
    },

    allInternalSelected () {
      return this.internalAccountRows.length > 0 && this.selectedInternalAccounts.length === this.internalAccountRows.length
    },

    allExternalSelected () {
      return this.externalReportRows.length > 0 && this.selectedExternalReports.length === this.externalReportRows.length
    },

    canCreateComposite () {
      return this.selectedInternalAccounts.length > 0 || this.selectedExternalReports.length > 0
    },

    anyCompositeProcessing () {
      return this.composites.some(r =>
        r.status === EudrBatchStatus.Pending.value || r.status === EudrBatchStatus.Processing.value)
    },

    internalHeaders () {
      return [
        { text: this.$t('account'), value: 'displayName', sortable: true, align: 'start' }
      ]
    },

    externalHeaders () {
      // No date-range column: every upload here shares the manager's selected period.
      return [
        { text: this.$t('account'), value: 'accountName', sortable: true, align: 'start' },
        { text: this.$t('eudrSourceFile'), value: 'sourceFileName', sortable: false, align: 'start', format: v => v || this.$t('notAvailable') },
        { text: this.$t('status'), value: 'status', sortable: false, align: 'center' },
        { text: this.$t('viewDetails'), value: 'actions', sortable: false, align: 'center' }
      ]
    },

    externalCustomColumns () {
      return [
        { slotName: 'status', value: 'status' },
        { slotName: 'actions', value: 'actions' }
      ]
    },

    missingHeaders () {
      return [
        { text: this.$t('account'), value: 'displayName', sortable: true, align: 'start' },
        { text: this.$t('attachExternal'), value: 'actions', sortable: false, align: 'center' }
      ]
    },

    missingCustomColumns () {
      return [{ slotName: 'actions', value: 'actions' }]
    },

    compositeHeaders () {
      return [
        { text: this.$t('created'), value: 'createdAt', sortable: true, align: 'start', format: v => (v ? utcToLocalDate(v, 'MM/DD/YYYY h:mm A') : this.$t('notAvailable')) },
        { text: this.$t('compositeLabel'), value: 'label', sortable: true, align: 'start', format: v => v || this.$t('untitled') },
        { text: this.$t('dateRange'), value: 'window', sortable: false, align: 'start' },
        { text: this.$t('status'), value: 'status', sortable: false, align: 'center' },
        { text: this.$t('actions'), value: 'actions', sortable: false, align: 'center' }
      ]
    },

    compositeCustomColumns () {
      return [
        { slotName: 'status', value: 'status' },
        { slotName: 'actions', value: 'actions' }
      ]
    }
  },

  watch: {
    year () { this.onPeriodChanged() },
    month () { this.onPeriodChanged() }
  },

  created () {
    if (this.allAccounts.length === 0) this.fetchAccounts([])
    this.loadAll()
  },

  beforeDestroy () {
    this.clearPoll()
  },

  methods: {
    ...mapActions('eudr', ['fetchBatches', 'fetchBatchById', 'fetchManagerOverview', 'deleteBatch', 'createComposite', 'requestEudrFile']),
    ...mapActions('account', ['fetchAccounts']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    accountName (accountId) {
      return this.allAccounts.find(a => a.accountId === accountId)?.name || '-'
    },

    windowLabel (report) {
      const start = report.startDate ? utcToLocalDate(report.startDate, 'MM/DD/YYYY') : ''
      const end = report.endDate ? windowEndDisplay(report.endDate) : ''
      return `${start} – ${end}`
    },

    onPeriodChanged () {
      this.selectedInternalAccounts = []
      this.selectedExternalReports = []
      this.loadAll()
    },

    async loadAll () {
      // Restart the polling budget, or an exhausted MAX_POLL_ATTEMPTS leaves polling off for good.
      this.pollAttempts = 0
      this.pollTimedOut = false
      await Promise.all([this.loadOverview(), this.loadComposites()])
    },

    async loadOverview () {
      this.overviewLoading = true
      try {
        const overview = await this.fetchManagerOverview({ startDate: this.period.startDate, endDate: this.period.endDate })
        this.internalAccounts = overview.internalAccounts ?? []
        this.externalReports = overview.externalReports ?? []
        this.missingExternalAccounts = overview.missingExternalAccounts ?? []
      } catch (e) {
      } finally {
        this.overviewLoading = false
      }
    },

    async loadComposites () {
      this.compositesLoading = true
      try {
        const reports = await this.fetchBatches()
        // Server dates lack a UTC suffix; moment.utc avoids local-time misparse.
        const { startDate, endDate } = this.period
        this.composites = reports.filter(r =>
          r.kind === EudrBatchKind.Composite.value &&
          moment.utc(r.startDate).valueOf() < moment.utc(endDate).valueOf() &&
          moment.utc(r.endDate).valueOf() > moment.utc(startDate).valueOf())
      } catch (e) {
      } finally {
        this.compositesLoading = false
      }
      this.scheduleNextPoll()
    },

    toggleSelectAllInternal () {
      this.selectedInternalAccounts = this.allInternalSelected ? [] : [...this.internalAccountRows]
    },

    toggleSelectAllExternal () {
      this.selectedExternalReports = this.allExternalSelected ? [] : [...this.externalReportRows]
    },

    openCompositePrompt () {
      if (!this.canCreateComposite) return
      this.promptingComposite = true
      this.compositeLabel = ''
      this.openOrUpdateDialog({ id: this.compositeDialogId, width: '420px' })
    },

    cancelComposite () {
      this.promptingComposite = false
      this.closeDialogsAtOrAbove(this.compositeDialogId)
    },

    async createCompositePackage () {
      if (!this.compositeLabel || !this.canCreateComposite) return
      this.compositing = true
      try {
        await this.createComposite({
          label: this.compositeLabel,
          startDate: this.period.startDate,
          endDate: this.period.endDate,
          sourceBatchIds: this.selectedExternalReports.map(r => r.eudrBatchId),
          internalAccountIds: this.selectedInternalAccounts.map(a => a.accountId)
        })
        // The composite is queued for the function app; it appears Pending and polling flips it to Complete.
        // Success snackbar fired by the response interceptor (customResponses.composite).
        this.selectedInternalAccounts = []
        this.selectedExternalReports = []
        this.cancelComposite()
        await this.loadComposites()
      } catch (e) {
        // Server validation (ENOSR/ERBNF/ESRNF/DAHBQ) surfaced by the error interceptor; keep the prompt open.
      } finally {
        this.compositing = false
      }
    },

    openUploadForMissing (account) {
      this.uploadingForAccount = account
      this.openOrUpdateDialog({ id: this.uploadDialogId, width: '720px' })
    },

    cancelUpload () {
      this.uploadingForAccount = null
      this.closeDialogsAtOrAbove(this.uploadDialogId)
    },

    async onUploadedForMissing () {
      this.cancelUpload()
      await this.loadOverview()
    },

    // A composite is usable once the daemon produced its file, validated/finalized or not.
    hasResult (item) {
      return hasUsableResult(item)
    },

    openReview (item) {
      if (!this.hasResult(item)) return
      this.reviewingReport = item
      this.openOrUpdateDialog({ id: this.validationDialogId, width: '95vw' })
    },

    // Requests the close; the Dialog runs the confirm-close flow and emits close if accepted.
    closeReview () {
      this.closeDialogsAtOrAbove(this.validationDialogId)
    },

    onReviewClosed () {
      this.reviewingReport = null
    },

    async onValidationSaved () {
      // Clearing the report first drops confirmClose, so the saved path closes without prompting.
      this.reviewingReport = null
      this.closeReview()
      await this.loadComposites()
    },

    async openDetails (item) {
      if (!this.hasResult(item)) return
      this.loadingDetails = true
      try {
        const [{ tracts, featureCollection }, full] = await Promise.all([
          // Prefer the validated copy so the preview reflects the user's current decisions.
          loadReportDetail(item.validatedBlobPath || item.resultBlobPath),
          this.fetchBatchById(item.eudrBatchId)
        ])
        this.detailTracts = tracts
        this.detailFeatureCollection = featureCollection
        this.detailSources = full?.sources ?? []
        this.detailTitle = `${item.label || item.accountName || this.$t('untitled')} — ${this.windowLabel(item)}`
        this.viewingDetails = true
        this.openOrUpdateDialog({ id: this.detailDialogId, width: '90vw' })
      } catch (e) {
        this.setSnackError(this.$t('eudrDownloadFailed'))
      } finally {
        this.loadingDetails = false
      }
    },

    closeDetails () {
      this.viewingDetails = false
      this.detailTracts = []
      this.detailFeatureCollection = null
      this.detailSources = []
      this.closeDialogsAtOrAbove(this.detailDialogId)
    },

    isFinalized (item) {
      return item.status === EudrBatchStatus.Finalized.value && !!item.sanitizedBlobPath
    },

    finalizedDateLabel (item) {
      return item.finalizedAt ? utcToLocalDate(item.finalizedAt, 'MM/DD/YYYY') : ''
    },

    // The finalized EU submission file — the exact persisted bytes, so re-downloads are stable.
    async downloadSubmission (item) {
      if (!this.isFinalized(item)) return
      this.downloading = true
      try {
        const blob = await eudrClient.fetchReportBlob(item.sanitizedBlobPath)
        saveAs(blob, this.fileNameFor(item, 'submission'))
      } catch (e) {
        this.setSnackError(this.$t('eudrDownloadFailed'))
      } finally {
        this.downloading = false
      }
    },

    // The rich Angel-to-Angel exchange flavor, derived on the fly from the current validated state.
    async downloadShare (item) {
      if (!this.hasResult(item)) return
      this.downloading = true
      try {
        const blob = await eudrClient.fetchReportBlob(item.validatedBlobPath || item.resultBlobPath)
        const parsed = JSON.parse(await blob.text())
        const excluded = new Set((parsed.features ?? [])
          .filter(f => f.properties?.excluded === true)
          .map(f => f.properties.featureId))
        const share = buildShareFile(parsed, excluded)
        saveAs(new Blob([JSON.stringify(share)], { type: 'application/json' }), this.fileNameFor(item, 'share'))
      } catch (e) {
        this.setSnackError(this.$t('eudrDownloadFailed'))
      } finally {
        this.downloading = false
      }
    },

    fileNameFor (item, suffix = '') {
      const base = item.label || this.$t('untitled')
      const name = base.replaceAll(/\s/g, '_').replaceAll(/[<>:"/\\|?*.]+/g, '_')
      const period = item.startDate ? utcToLocalDate(item.startDate, 'YYMM') : ''
      return `EUDR_${name}_${period}${suffix ? `_${suffix}` : ''}.json`
    },

    openSendDialog (item) {
      if (!this.isFinalized(item)) return
      this.sendingReport = item
      this.openOrUpdateDialog({ id: this.sendDialogId, width: '520px' })
    },

    closeSendDialog () {
      this.sendingReport = null
      this.closeDialogsAtOrAbove(this.sendDialogId)
    },

    promptRequestFile (item) {
      this.requestingFileFor = item
      this.openOrUpdateDialog({ id: this.requestDialogId, width: '400px' })
    },

    cancelRequestFile () {
      this.requestingFileFor = null
      this.closeDialogsAtOrAbove(this.requestDialogId)
    },

    // Fire-and-forget by design: no request history is kept.
    async sendFileRequest () {
      const account = this.requestingFileFor
      this.cancelRequestFile()
      if (!account) return
      try {
        const result = await this.requestEudrFile({
          accountId: account.accountId,
          startDate: this.period.startDate,
          endDate: this.period.endDate
        })
        this.setSnack(this.$t('eudrRequestFileSent', { count: result.queuedEmailCount, account: account.name }))
      } catch (e) {
      }
    },

    promptDelete (item) {
      this.reportToDelete = item
      this.openOrUpdateDialog({ id: this.deleteDialogId, width: '400px' })
    },

    cancelDelete () {
      this.reportToDelete = null
      this.closeDialogsAtOrAbove(this.deleteDialogId)
    },

    async deleteSelectedReport () {
      if (!this.reportToDelete) return
      this.deleting = true
      try {
        await this.deleteBatch(this.reportToDelete.eudrBatchId)
        this.cancelDelete()
        await this.loadComposites()
      } catch (e) {
        // Keep the dialog open so the user can retry.
      } finally {
        this.deleting = false
      }
    },

    scheduleNextPoll () {
      this.clearPoll()
      if (!this.anyCompositeProcessing) {
        this.pollAttempts = 0
        this.pollTimedOut = false
        return
      }
      if (this.pollAttempts >= MAX_POLL_ATTEMPTS) {
        this.pollTimedOut = true
        return
      }
      this.pollTimer = setTimeout(() => {
        this.pollAttempts++
        this.pollProcessingComposites()
      }, POLL_INTERVAL_MS)
    },

    // Only the in-flight rows can change on this timescale, so poll those by id instead of re-fetching the list.
    async pollProcessingComposites () {
      const inflight = this.composites.filter(r =>
        r.status === EudrBatchStatus.Pending.value || r.status === EudrBatchStatus.Processing.value)
      const updates = await Promise.all(inflight.map(r => this.fetchBatchById(r.eudrBatchId).catch(() => null)))
      for (const updated of updates) {
        if (!updated) continue
        const index = this.composites.findIndex(r => r.eudrBatchId === updated.eudrBatchId)
        if (index !== -1) this.composites.splice(index, 1, updated)
      }
      this.scheduleNextPoll()
    },

    clearPoll () {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    }
  }
}
</script>

<style scoped>
.eudr-manager {
  min-height: 90vh;
}
.eudr-period-select {
  width: 110px;
}
/* "September" plus the dropdown arrow overflows the compact width and wraps the selection. */
.eudr-period-select--month {
  width: 150px;
}
</style>
